import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateQueriesFor, tenantsEndpoint } from "@visioni/a3s-dir/app";
import { useImpersonationStore } from "../impersonation-store";

/**
 * Settings-facing impersonation actions.
 * Wrap store writes with tenant-query invalidation so "View As" changes
 * always refresh directory tenant membership for the active identity.
 */
export function useImpersonationSettings() {
  const queryClient = useQueryClient();
  const impersonatedUserId = useImpersonationStore((s) => s.impersonatedUserId);
  const impersonatedLabel = useImpersonationStore((s) => s.impersonatedLabel);
  const setImpersonationState = useImpersonationStore((s) => s.setImpersonation);
  const clearImpersonationState = useImpersonationStore((s) => s.clearImpersonation);

  const invalidateTenantQuery = useCallback(() => {
    void queryClient.invalidateQueries(invalidateQueriesFor(tenantsEndpoint));
  }, [queryClient]);

  const setImpersonation = useCallback(
    (userId: string, label: string) => {
      setImpersonationState(userId, label);
      invalidateTenantQuery();
    },
    [setImpersonationState, invalidateTenantQuery],
  );

  const clearImpersonation = useCallback(() => {
    clearImpersonationState();
    invalidateTenantQuery();
  }, [clearImpersonationState, invalidateTenantQuery]);

  return {
    impersonatedUserId,
    impersonatedLabel,
    setImpersonation,
    clearImpersonation,
  };
}
